import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useMemo } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useScreenRenderContext, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef } from '@micromag/core/contexts';
import { useDimensionObserver } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, isTextFilled } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout from '@micromag/element-layout';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { Image, Heading1, Text as Text$1, Container as Container$1 } from '@micromag/transforms/apple-news';

var styles = {"container":"micromag-screen-image-container","image":"micromag-screen-image-image","background":"micromag-screen-image-background","disabled":"micromag-screen-image-disabled","hidden":"micromag-screen-image-hidden","placeholder":"micromag-screen-image-placeholder","content":"micromag-screen-image-content","emptyImage":"micromag-screen-image-emptyImage","emptyTitle":"micromag-screen-image-emptyTitle","emptyText":"micromag-screen-image-emptyText","emptyLegend":"micromag-screen-image-emptyLegend","imageContainer":"micromag-screen-image-imageContainer","placeholderImage":"micromag-screen-image-placeholderImage","isPlaceholder":"micromag-screen-image-isPlaceholder","layout":"micromag-screen-image-layout","isCard":"micromag-screen-image-isCard","isCardReverse":"micromag-screen-image-isCardReverse","footer":"micromag-screen-image-footer","isFullscreen":"micromag-screen-image-isFullscreen","header":"micromag-screen-image-header"};

// NOTE: should this be a scrolling element ?

var propTypes = {
  layout: PropTypes.oneOf(['normal', 'fullscreen', 'reverse', 'card', 'card-reverse', 'title-top']),
  image: PropTypes$1.imageMedia,
  imageFit: PropTypes.oneOf(['contain', 'cover']),
  defaultImageFit: PropTypes.string,
  title: PropTypes$1.headingElement,
  text: PropTypes$1.textElement,
  legend: PropTypes$1.textElement,
  withTitle: PropTypes.bool,
  withText: PropTypes.bool,
  withLegend: PropTypes.bool,
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'normal',
  image: null,
  imageFit: null,
  defaultImageFit: 'cover',
  title: null,
  text: null,
  legend: null,
  withTitle: false,
  withText: false,
  withLegend: false,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  className: null
};
var ImageScreen = function ImageScreen(_ref) {
  var layout = _ref.layout,
    image = _ref.image,
    imageFit = _ref.imageFit,
    defaultImageFit = _ref.defaultImageFit,
    title = _ref.title,
    text = _ref.text,
    legend = _ref.legend,
    withTitle = _ref.withTitle,
    withText = _ref.withText,
    withLegend = _ref.withLegend,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var hasImage = image !== null;
  var hasTitle = isTextFilled(title);
  var hasText = isTextFilled(text);
  var hasLegend = isTextFilled(legend);
  var _useState = useState(!hasImage),
    _useState2 = _slicedToArray(_useState, 2),
    ready = _useState2[0],
    setReady = _useState2[1];
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || active;
  var onImageLoaded = useCallback(function () {
    setReady(true);
  }, [setReady]);
  var finalImageFit = useMemo(function () {
    return {
      fit: imageFit || defaultImageFit
    };
  }, [imageFit, defaultImageFit]);
  var isReversed = layout === 'reverse' || layout === 'card-reverse';
  var isTitleTop = layout === 'title-top';
  var isCardLayout = layout === 'card' || layout === 'card-reverse';
  var isCard = layout === 'card';
  var isCardReverse = layout === 'card-reverse';
  var isFullscreen = layout === 'fullscreen';
  var finalSpacing = !isFullscreen && !isPlaceholder ? spacing : 0;
  var _useDimensionObserver = useDimensionObserver(),
    imageCntRef = _useDimensionObserver.ref,
    imageWidth = _useDimensionObserver.width,
    imageHeight = _useDimensionObserver.height;
  var cardImageMargin = isCardReverse ? "".concat(finalSpacing / 2, "px ").concat(-finalSpacing / 2, "px 0") : "0 ".concat(-finalSpacing / 2, "px ").concat(finalSpacing / 2, "px");
  var imageMargin = isCardLayout ? cardImageMargin : finalSpacing / 2;
  var itemMarginStyle = !isPlaceholder ? {
    margin: finalSpacing / 2,
    marginTop: isCardReverse && !hasHeader ? finalSpacing : finalSpacing / 2,
    marginBottom: isCard && !hasHeader ? finalSpacing : finalSpacing / 2
  } : null;
  var items = [/*#__PURE__*/React.createElement("div", {
    key: "image",
    ref: imageCntRef,
    className: styles.imageContainer,
    style: !isPlaceholder ? {
      margin: imageMargin
    } : null
  }, /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "image",
    placeholderProps: {
      className: styles.placeholderImage,
      height: '100%'
    },
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "ebsPd4",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    }),
    emptyClassName: styles.emptyImage,
    isEmpty: !hasImage
  }, hasImage ? /*#__PURE__*/React.createElement(Visual, {
    className: styles.image,
    media: image,
    objectFit: finalImageFit,
    width: imageWidth,
    height: imageHeight,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    active: active,
    shouldLoad: mediaShouldLoad,
    withoutVideo: isPreview,
    onLoaded: onImageLoaded
  }) : null)), withTitle && /*#__PURE__*/React.createElement(ScreenElement, {
    key: "title",
    placeholder: "title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "2ZOPe+",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: styles.emptyTitle,
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement("div", {
    style: itemMarginStyle
  }, /*#__PURE__*/React.createElement(Heading, title)) : null), withText && /*#__PURE__*/React.createElement(ScreenElement, {
    key: "text",
    placeholder: "text",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "z4CiV1",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    }),
    emptyClassName: styles.emptyText,
    isEmpty: !hasText
  }, hasText ? /*#__PURE__*/React.createElement("div", {
    style: itemMarginStyle
  }, /*#__PURE__*/React.createElement(Text, text)) : null), withLegend && /*#__PURE__*/React.createElement(ScreenElement, {
    key: "legend",
    placeholder: "shortText",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "dqnkQk",
      defaultMessage: [{
        "type": 0,
        "value": "Legend"
      }]
    }),
    emptyClassName: styles.emptyLegend,
    isEmpty: !hasLegend
  }, hasLegend ? /*#__PURE__*/React.createElement("div", {
    style: itemMarginStyle
  }, /*#__PURE__*/React.createElement(Text, legend)) : null)];
  if (isReversed) {
    items.reverse();
  } else if (isTitleTop) {
    if (withTitle && (hasTitle || isPlaceholder)) {
      items.splice(0, 0, items.splice(1, 1)[0]);
    }
  }
  var paddingBottom = (current && !isPreview ? viewerBottomHeight : 0) + finalSpacing / 2;
  var paddingTop = (!isPreview ? viewerTopHeight : 0) + finalSpacing / 2;
  if (isCardLayout || isFullscreen) {
    paddingTop = 0;
    paddingBottom = 0;
  }
  if (isCardReverse) {
    paddingTop = current && !isPreview ? viewerTopHeight : 0;
    paddingBottom = current && !isPreview ? viewerBottomHeight : 0;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.isReversed, isReversed), styles.isPlaceholder, isPlaceholder), styles.isCard, isCard), styles.isCardReverse, isCardReverse), styles.isFullscreen, isFullscreen)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    fullscreen: true,
    style: !isPlaceholder ? {
      padding: finalSpacing / 2,
      paddingTop: paddingTop,
      paddingBottom: paddingBottom
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    className: styles.header,
    style: isFullscreen || isCardReverse ? {
      paddingTop: hasHeader ? spacing / 2 : spacing,
      paddingBottom: hasHeader ? spacing / 2 : spacing,
      paddingLeft: isCardReverse ? spacing / 2 : spacing,
      paddingRight: isCardReverse ? spacing / 2 : spacing,
      transform: !isPreview ? "translate(0, ".concat(viewerTopHeight, "px)") : null
    } : {
      paddingTop: isCardLayout ? spacing / 2 : null,
      paddingBottom: isCardLayout ? spacing : spacing / 2,
      paddingLeft: spacing / 2,
      paddingRight: spacing / 2
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, items, !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: styles.footer,
    style: isFullscreen || isCardReverse ? {
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingTop: spacing / 2,
      paddingBottom: spacing / 2,
      transform: !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null
    } : {
      paddingLeft: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingRight: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingTop: isCardLayout ? spacing / 2 : null,
      paddingBottom: isCardLayout ? spacing / 2 : null
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
ImageScreen.propTypes = propTypes;
ImageScreen.defaultProps = defaultProps;

var ImageLegendScreen = function ImageLegendScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(ImageScreen, Object.assign({}, props, {
    withLegend: true
  }));
};

var ImageTextScreen = function ImageTextScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(ImageScreen, Object.assign({}, props, {
    withText: true
  }));
};

var ImageTitleScreen = function ImageTitleScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(ImageScreen, Object.assign({}, props, {
    withTitle: true
  }));
};

var ImageTitleTextScreen = function ImageTitleTextScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(ImageScreen, Object.assign({}, props, {
    withTitle: true,
    withText: true
  }));
};

var transform = function transform(newStory, _ref) {
  var layout = _ref.layout,
    image = _ref.image,
    text = _ref.text,
    title = _ref.title;
  var _Image = Image(newStory, image),
    imageStory = _Image.story,
    imageComponent = _Image.component;
  var _Heading = Heading1(imageStory, title),
    titleStory = _Heading.story,
    titleComponent = _Heading.component;
  var _Text = Text$1(titleStory, text),
    textStory = _Text.story,
    textComponent = _Text.component;
  var items = [];
  switch (layout) {
    case 'reverse':
      items = [].concat(_toConsumableArray(textComponent ? [textComponent] : []), _toConsumableArray(titleComponent ? [titleComponent] : []), _toConsumableArray(imageComponent ? [imageComponent] : []));
      break;
    case 'title-top':
      items = [].concat(_toConsumableArray(titleComponent ? [titleComponent] : []), _toConsumableArray(imageComponent ? [imageComponent] : []), _toConsumableArray(textComponent ? [textComponent] : []));
      break;
    default:
      items = [].concat(_toConsumableArray(imageComponent ? [imageComponent] : []), _toConsumableArray(titleComponent ? [titleComponent] : []), _toConsumableArray(textComponent ? [textComponent] : []));
      break;
  }
  var _Container = Container$1(textStory, items),
    containerStory = _Container.story,
    component = _Container.component;
  return _objectSpread(_objectSpread({}, containerStory), {}, {
    components: [].concat(_toConsumableArray(newStory.components || []), _toConsumableArray(component ? [component] : []))
  });
};

// eslint-disable-next-line

var transforms = /*#__PURE__*/Object.freeze({
    __proto__: null,
    appleNews: transform
});

var definition = [{
  id: 'image',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "uJ9eBc",
    defaultMessage: [{
      "type": 0,
      "value": "Image"
    }]
  }),
  component: ImageScreen,
  layouts: ['normal', 'fullscreen'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'normal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'image',
    type: 'visual',
    label: defineMessage({
      id: "xsipID",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }, {
    name: 'imageFit',
    type: 'fit',
    defaultValue: 'cover',
    values: ['cover', 'contain'],
    label: defineMessage({
      id: "dkBBRa",
      defaultMessage: [{
        "type": 0,
        "value": "Image fit"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'image-title',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "S/mavZ",
      defaultMessage: [{
        "type": 0,
        "value": "Text and Images"
      }]
    }),
    order: 3
  },
  title: defineMessage({
    id: "AA8TV9",
    defaultMessage: [{
      "type": 0,
      "value": "Image with title"
    }]
  }),
  component: ImageTitleScreen,
  layouts: ['normal', 'reverse', 'card', 'card-reverse'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'normal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'image',
    type: 'visual',
    label: defineMessage({
      id: "xsipID",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }, {
    name: 'imageFit',
    type: 'fit',
    defaultValue: 'cover',
    values: ['cover', 'contain'],
    label: defineMessage({
      id: "dkBBRa",
      defaultMessage: [{
        "type": 0,
        "value": "Image fit"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    inline: true,
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'image-text',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "S/mavZ",
      defaultMessage: [{
        "type": 0,
        "value": "Text and Images"
      }]
    }),
    order: 3
  },
  title: defineMessage({
    id: "CJmQeh",
    defaultMessage: [{
      "type": 0,
      "value": "Image with text"
    }]
  }),
  component: ImageTextScreen,
  layouts: ['normal', 'reverse', 'card', 'card-reverse'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'normal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'image',
    type: 'visual',
    label: defineMessage({
      id: "xsipID",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }, {
    name: 'imageFit',
    type: 'fit',
    defaultValue: 'cover',
    values: ['cover', 'contain'],
    label: defineMessage({
      id: "dkBBRa",
      defaultMessage: [{
        "type": 0,
        "value": "Image fit"
      }]
    })
  }, {
    name: 'text',
    type: 'text-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "4E2gbX",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'image-title-text',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "S/mavZ",
      defaultMessage: [{
        "type": 0,
        "value": "Text and Images"
      }]
    }),
    order: 3
  },
  title: defineMessage({
    id: "eWIM/8",
    defaultMessage: [{
      "type": 0,
      "value": "Image with title and text"
    }]
  }),
  component: ImageTitleTextScreen,
  layouts: ['normal', 'reverse', 'title-top', 'card', 'card-reverse'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'normal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'image',
    type: 'visual',
    isHorizontal: false,
    label: defineMessage({
      id: "xsipID",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }, {
    name: 'imageFit',
    type: 'fit',
    defaultValue: 'cover',
    values: ['cover', 'contain'],
    label: defineMessage({
      id: "dkBBRa",
      defaultMessage: [{
        "type": 0,
        "value": "Image fit"
      }]
    })
  }, {
    name: 'title',
    type: 'text-element',
    inline: true,
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'text',
    type: 'text-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "4E2gbX",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'image-legend',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "H1YcaV",
    defaultMessage: [{
      "type": 0,
      "value": "Image with legend"
    }]
  }),
  component: ImageLegendScreen,
  layouts: ['normal', 'reverse', 'card', 'card-reverse'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'normal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'image',
    type: 'visual',
    label: defineMessage({
      id: "xsipID",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }, {
    name: 'imageFit',
    type: 'fit',
    defaultValue: 'cover',
    values: ['cover', 'contain'],
    label: defineMessage({
      id: "dkBBRa",
      defaultMessage: [{
        "type": 0,
        "value": "Image fit"
      }]
    })
  }, {
    name: 'legend',
    type: 'text-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "3OS3Ms",
      defaultMessage: [{
        "type": 0,
        "value": "Legend"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { ImageLegendScreen, ImageScreen, ImageTextScreen, ImageTitleScreen, ImageTitleTextScreen, definition as default };
